// src/customReports/choosing-the-right-learning-partner.js

import React, { useRef, useEffect, useState } from 'react';
import ChartJS from 'chart.js/auto';

export default function Q5Report() {
  const [showMethodology, setShowMethodology] = useState(false);
  const jobRef = useRef(null);
  const jobChart = useRef(null);

  useEffect(() => {
    if (!showMethodology) return;
    const canvas = jobRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (jobChart.current) jobChart.current.destroy();

    const labels = [
      'CHRO / HR Director',
      'HRBP',
      'Talent Development Leader',
      'L&D Specialist',
      'C-suite',
      'Org Dev',
      'CLO / Head of L&D',
      'Other'
    ];
    const values = [21, 19, 16, 12, 11, 9, 4, 2];
    const baseColor = [5, 63, 85];

    jobChart.current = new ChartJS(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          label: '% of respondents',
          data: values,
          backgroundColor: values.map((_, i) => {
            const alpha = Math.max(0.2, 1 - i * 0.1);
            return `rgba(${baseColor.join(',')},${alpha})`;
          }),
          barThickness: 24
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { beginAtZero: true, ticks: { callback: v => `${v}%` }, grid: { drawBorder: false, color: '#eee' } },
          y: { grid: { drawBorder: false, color: '#eee' } }
        },
        plugins: { legend: { display: false } }
      }
    });

    return () => {
      jobChart.current?.destroy();
      jobChart.current = null;
    };
  }, [showMethodology]);

  return (
    <section className="q5-report">

      {/* Why Download This Report */}
      <section className="why-it-matters">
        <h2>How global talent leaders select learning partners—and what shapes their decisions</h2>
        <p className="why-text">
          Today’s learning partner landscape is crowded—and expectations are rising. This report, developed in partnership with UNICON, cuts through the complexity with fresh data from 200+ global HR and L&D leaders, revealing what drives provider selection, where different models stand out, and where the biggest opportunity gaps remain.
With clear heatmaps, interview insight from firms like SAP, Amazon, Aramco and Airbus, and practical cues for both buyers and providers, it’s a go-to guide for anyone navigating learning investments or designing for impact.</p>
<p><strong>Ready to dive in? Complete the form below to download the full report.</strong></p>
      </section>

      {/* Five Key Questions */}
      <section className="key-questions">
        <h3>The five questions this report answers</h3>
        <ol className="message-list">
          <li><div className="message">1. What do today’s buyers value most—and how does that shape their selection process?</div></li>
          <li><div className="message">2. Where do business schools, coaching firms, consultancies, and others perform strongest—and for which needs?</div></li>
          <li><div className="message">3. Which skills and learning priorities are rising to the top in 2025?</div></li>
          <li><div className="message">4. What gaps exist between provider performance and buyer need?</div></li>
          <li><div className="message">5. What does a true strategic learning partnership look like—and how can it be achieved?</div></li>
        </ol>
      </section>

      {/* Preview Insights */}
      <section className="preview-insights">
        <h3>Preview insights</h3>
        <div className="insight-cards">
          <div className="insight-card">
            <i className="fa-regular fa-goal-net"></i>
            <p>24% of leaders rank <strong>alignment with business goals</strong> as their top selection criterion</p>
          </div>
          <div className="insight-card">
            <i className="fa-regular fa-layer-plus"></i>
            <p>Flexibility and co-creation are consistently cited as <strong>make-or-break qualities</strong></p>
          </div>
          <div className="insight-card">
            <i className="fa-regular fa-building-columns"></i>
            <p>Business schools lead in <strong>high-stakes strategic areas</strong>, while coaching firms are diversifying at pace</p>
          </div>
          <div className="insight-card">
            <i className="fa-regular fa-swatchbook"></i>
            <p>Customization, follow-up, and personalization at scale <strong>remain major opportunities</strong> for differentiation</p>
          </div>
          <div className="insight-card">
            <i className="fa-regular fa-ranking-star"></i>
            <p>Only 5% of leaders rank reputation as their top selection criterion—fit, <strong>flexibility, and trust matter more</strong></p>
          </div>
        </div>
      </section>

      {/* Who This Report Is For */}
      <section className="report-for">
        <h3>Who this report is for</h3>
        <div className="report-for-list">
          <div className="tick-item">
            <i className="fa-regular fa-people-roof"></i>
            <div>
              <h3>Talent and Learning Leaders</h3>
              <p>Making critical partner decisions and under pressure to prove impact.</p>
            </div>
          </div>
          <div className="tick-item">
            <i className="fa-regular fa-briefcase"></i>
            <div>
              <h3>Learning Providers &amp; Consultancies</h3>
              <p>Aiming to align more closely to client priorities and enhance market fit.</p>
            </div>
          </div>
          <div className="tick-item">
            <i className="fa-regular fa-chess-knight"></i>
            <div>
              <h3>Strategy and Innovation Teams</h3>
              <p>Exploring trends and identifying opportunities in executive development.</p>
            </div>
          </div>
          <div className="tick-item">
            <i className="fa-regular fa-chart-mixed-up-circle-dollar"></i>
            <div>
              <h3>Investors and Analysts</h3>
              <p>Tracking shifts and evaluating potential in the corporate learning ecosystem.</p>
            </div>
          </div>
        </div>
      </section>

      {/* About This Research */}
      <div className="about-research-section">
        <div>
          <button
            className={`methodology-toggle ${showMethodology ? 'open' : ''}`}
            onClick={() => setShowMethodology(v => !v)}
          >
            About This Research <i className="fa-regular fa-chevron-down"></i>
          </button>

          {showMethodology && (
            <div className="methodology-content">
              <p>
                This research combines the scale of quantitative data with the depth of qualitative insight to offer a systemic view of how learning partner choices are made today. The survey reveals patterns and priorities at scale, while the interviews add colour, contextual richness, and the voice of experience—helping to make sense of the numbers and bring the decision-making process to life.
              </p>

              <h4>Quantitative Survey</h4>
              <p><strong>Participants:</strong> 200 global learning and talent leaders</p>
              <h4>Job Roles</h4>
              <div className="job-chart-container">
              <canvas ref={jobRef} />
              </div>

              <table className="methodology-table">
                <thead>
                  <tr><th>Component</th><th>Details</th></tr>
                </thead>
                <tbody>
                  <tr><td>Industry Mix</td><td>Tech, Healthcare, Manufacturing, Education, Professional Services, Energy, Retail, and more</td></tr>
                  <tr><td>Data Collection</td><td>Structured online survey with ranking and selection questions across five key areas</td></tr>
                  <tr><td>Approach to Analysis</td><td>Quantitative aggregation of rankings and selections across provider types and criteria</td></tr>
                </tbody>
              </table>

              <h4>Qualitative Interviews</h4>
              <table className="methodology-table">
                <thead>
                  <tr><th>Component</th><th>Qualitative Interviews</th></tr>
                </thead>
                <tbody>
                  <tr><td>Participants</td><td>13 senior leaders (CPO, CHRO, CLO, VP, Director level)</td></tr>
                  <tr><td>Decision-Making Role</td><td>All participants are senior strategic decision-makers</td></tr>
                  <tr><td>Geographic Coverage</td><td>Five continents represented (Americas, EMEA, APAC, Middle East)</td></tr>
                  <tr><td>Job Roles</td><td>Range across HR, Talent, and L&D leadership roles</td></tr>
                  <tr><td>Industry Mix</td><td>Same diverse industry spread reflected in interview sample</td></tr>
                  <tr><td>Data Collection</td><td>45-minute in-depth interviews focused on decision process and partner perceptions</td></tr>
                  <tr><td>Approach to Analysis</td><td>Thematic coding of qualitative data, mapped to survey categories</td></tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
